import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Palette, Settings, ChevronRight, X, Rocket, Flag, Flame, Sparkles, LogIn, ImagePlus, Upload, Users, CheckCircle2, Compass, User as UserIcon, Smartphone } from 'lucide-react';
import XPBar from '../components/XPBar';
import HomeArtworkShowcase, { prefetchHomeWallPreview } from '../components/HomeArtworkShowcase';
import InspirationViewerModal from '../components/InspirationViewerModal';
import ContributorBadge from '../components/ContributorBadge';
import NewColoringProjectModal from '../components/NewColoringProjectModal';
import Bongo from '../components/Bongo';
import WhatsNewSection from '../components/WhatsNewSection';
import PauseNoticeCard from '../components/PauseNoticeCard';
import { useActiveProjects } from '../hooks/useColoringProjects';
import { cName, cDesc } from '../data/challenges';
import { useAuth, hasUserEverBeenAuthenticatedOnDevice } from '../hooks/useAuth';
import { getDailyBongoMessage } from '../data/bongoMessages';
import { useSupporterStatus } from '../services/supporterService';
import { getLocalStudioInspirations, INSPIRATION_HISTORY_UPDATED_EVENT } from '../services/studioInspirationService';
import { useSectionTheme } from '../hooks/useSectionTheme';
import { loadProgress as loadColorMemoryProgress } from '../games/color-memory/colorMemoryStorage';
import { supabase } from '../lib/supabase';

// --- Carte jeux (PROMPT_62) : helpers de chargement ---
const SHADE_STORAGE_KEY = 'coloBongo_shadeMatch_progress';
const GAMES_LEVEL_MAX = 60;
const GAMES_FALLBACK_DAYS = 30;

async function loadShadeProgress(user) {
  if (user && supabase) {
    try {
      const { data } = await supabase
        .from('user_game_progress')
        .select('progress_data')
        .eq('user_id', user.id)
        .eq('game_key', 'shade_match')
        .single();
      if (data?.progress_data) return data.progress_data;
    } catch (e) {
      // ignore
    }
  }
  try {
    const stored = localStorage.getItem(SHADE_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    // ignore
  }
  return null;
}

// Fonction pure : determine l'etat de la carte jeux a partir des progressions chargees.
// Retourne null si chargement en cours, { type: 'resume', game } si jeu joue dans
// les 30 derniers jours, sinon { type: 'discover' }.
function getGamesCardState(gamesProgress) {
  if (!gamesProgress) return null;
  const { colorMemory, shadeMatch } = gamesProgress;
  const cmDate = colorMemory?.lastPlayedDate ? new Date(colorMemory.lastPlayedDate) : null;
  const smDate = shadeMatch?.lastPlayedDate ? new Date(shadeMatch.lastPlayedDate) : null;
  const now = new Date();
  const msPerDay = 86400000;
  const cmDaysAgo = cmDate && !isNaN(cmDate.getTime()) ? (now - cmDate) / msPerDay : Infinity;
  const smDaysAgo = smDate && !isNaN(smDate.getTime()) ? (now - smDate) / msPerDay : Infinity;
  let lastGame = null;
  if (cmDaysAgo <= GAMES_FALLBACK_DAYS || smDaysAgo <= GAMES_FALLBACK_DAYS) {
    if (cmDaysAgo <= smDaysAgo) {
      lastGame = {
        key: 'color_memory',
        route: '/games/color-memory',
        nameFr: "L'Oeil de Bongo",
        nameEn: "Bongo's Eye",
        icon: 'eye',
        level: Math.max(1, colorMemory?.currentLevel || 1),
        streak: colorMemory?.streak || 0,
      };
    } else {
      lastGame = {
        key: 'shade_match',
        route: '/games/shade-match',
        nameFr: 'Trouve la Nuance',
        nameEn: 'Find the Shade',
        icon: 'droplet',
        level: Math.max(1, shadeMatch?.currentLevel || 1),
        streak: shadeMatch?.streak || 0,
      };
    }
  }
  if (lastGame) return { type: 'resume', game: lastGame };
  return { type: 'discover' };
}

export default function Home({ t, language, progress, levelInfo, progressSource, isProgressSyncing }) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { isContributorActive } = useSupporterStatus();
  const sectionTheme = useSectionTheme();
  const { currentLevel, xpInLevel, xpForNextLevel } = levelInfo || { currentLevel: 1, xpInLevel: 0, xpForNextLevel: 100 };
  const [showEarlyAccessInfo, setShowEarlyAccessInfo] = useState(false);
  const [dailyPhrase, setDailyPhrase] = useState('');
  const [aiInspirations, setAiInspirations] = useState([]);
  const [viewingInspiration, setViewingInspiration] = useState(null);
  const [showNewColoringModal, setShowNewColoringModal] = useState(false);

  // Projets actifs du Carnet de couleurs — passes en `activeProjects` au
  // NewColoringProjectModal pour le check de quota. Plus consommes pour le
  // listing visuel sur la home (PROMPT_55 a recentre la section "On reprend
  // doucement" sur le seul bouton +Nouveau).
  const {
    projects: coloringProjects,
    reload: reloadColoring,
  } = useActiveProjects({ isContributor: !!isContributorActive, limit: 3 });
  const [dismissedOnboarding, setDismissedOnboarding] = useState(() => {
    try {
      return localStorage.getItem('colobongo_dismissed_onboarding') === 'true';
    } catch {
      return false;
    }
  });
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [hasVisitedInstall, setHasVisitedInstall] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const standalone = window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator?.standalone === true;
      setIsAppInstalled(Boolean(standalone));
    } catch {
      setIsAppInstalled(false);
    }
    try {
      setHasVisitedInstall(localStorage.getItem('colobongo_visited_install_v1') === '1');
    } catch {
      setHasVisitedInstall(true);
    }
  }, []);

  const reduceMotion = useReducedMotion();
  const showInstallPulse = !isAppInstalled && !hasVisitedInstall && !reduceMotion;

  // Determine if user is new (0 completed challenges or dismissed)
  // Also skip if user was ever authenticated on this device (they've seen it before)
  const isNewUser = !dismissedOnboarding && progress.challengesCompleted <= 1 && !hasUserEverBeenAuthenticatedOnDevice();

  useEffect(() => {
    setDailyPhrase(getDailyBongoMessage(language));
  }, [language]);

  useEffect(() => {
    const isMobileViewport =
      typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 639px)').matches;

    prefetchHomeWallPreview({ limit: isMobileViewport ? 5 : 7 }).catch(() => {});
  }, []);

  useEffect(() => {
    const loadInspirations = () => {
      if (!user?.id) {
        setAiInspirations([]);
        return;
      }
      setAiInspirations(getLocalStudioInspirations({ userId: user.id, limit: 2 }));
    };

    loadInspirations();

    const handleRefresh = () => loadInspirations();
    window.addEventListener('focus', handleRefresh);
    window.addEventListener('storage', handleRefresh);
    window.addEventListener(INSPIRATION_HISTORY_UPDATED_EVENT, handleRefresh);

    return () => {
      window.removeEventListener('focus', handleRefresh);
      window.removeEventListener('storage', handleRefresh);
      window.removeEventListener(INSPIRATION_HISTORY_UPDATED_EVENT, handleRefresh);
    };
  }, [user?.id]);

  // PROMPT_55 : la section "On reprend doucement" se reduit a un seul
  // bouton +Nouveau coloriage. Le listing des projets actifs et l'entree
  // Frigo (accessible via la BottomNav) ne sont plus rendus ici. Le hook
  // useActiveProjects ci-dessus reste pour alimenter le quota du modal
  // de creation.

  // PROMPT_62 : chargement des progressions des deux jeux pour la carte
  // adaptative "Reprends [jeu] / Decouvre les jeux". null = chargement en
  // cours -> la carte n'apparait pas (l'accueil reste fonctionnel).
  const [gamesProgress, setGamesProgress] = useState(null);
  useEffect(() => {
    if (!user) {
      setGamesProgress({ colorMemory: null, shadeMatch: null });
      return undefined;
    }
    let cancelled = false;
    Promise.all([
      loadColorMemoryProgress(user, supabase),
      loadShadeProgress(user),
    ])
      .then(([cm, sm]) => {
        if (!cancelled) {
          setGamesProgress({ colorMemory: cm, shadeMatch: sm });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGamesProgress({ colorMemory: null, shadeMatch: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);
  const gamesCardState = getGamesCardState(gamesProgress);

  const bongoMood = xpInLevel >= Math.max(1, Math.round(xpForNextLevel * 0.8)) ? 'happy' : 'encouragement';

  return (
    <div className="page-content">
      {/* Header avec gradient doux */}
      <div className="px-4 pt-6 pb-4">
        <div className="max-w-md md:max-w-xl mx-auto cozy-hero-surface cozy-signature-hero px-5 pt-5 pb-5"
             style={{
               borderRadius: 'var(--radius-2xl)',
               background: 'linear-gradient(135deg, rgba(255,248,245,0.95) 0%, rgba(253,237,230,0.9) 100%)',
               boxShadow: 'var(--shadow-[var(--cb-shadow-md)])',
             }}>
          {/* Boutons du haut */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0 flex-1">
              <motion.button
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                onClick={() => setShowEarlyAccessInfo(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-accent-sky)]/20 text-[color:var(--cb-text)] text-xs font-bold shadow-[var(--cb-shadow-sm)] border border-[color:var(--cb-border)] hover:bg-[color:var(--cb-accent-sky)]/30 transition-colors"
              >
                <Rocket size={13} />
                {language === 'fr' ? 'Accès anticipé' : 'Early Access'}
              </motion.button>
            </div>

            <motion.button
              onClick={() => navigate('/settings')}
              className="p-2.5 rounded-[var(--cb-radius-pill)] bg-white/90 border border-[var(--cb-border)] shadow-[var(--cb-shadow-sm)] cozy-pressable hover:shadow-[var(--cb-shadow-md)] transition-shadow"
              whileTap={{ scale: 0.9 }}
              data-testid="home-settings-button"
              aria-label={language === 'fr' ? 'Ouvrir les paramètres' : 'Open settings'}
            >
              <Settings size={18} className="text-[color:var(--cb-text-muted)]" />
            </motion.button>
          </div>

          {/* Section titre avec Bongo */}
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <p
                className="cozy-section-kicker mb-2"
                style={{ color: sectionTheme.text, transition: 'color 280ms var(--cb-ease-out)' }}
              >
                {language === 'fr' ? 'Maison ColoBongo' : 'ColoBongo home'}
              </p>
              <h1 className="text-[24px] leading-tight font-extrabold text-[color:var(--cb-text)]">
                {t('welcomeBack')}
              </h1>
              <p className="text-sm text-[color:var(--cb-text-muted)] mt-1.5 font-medium">
                {language === 'fr'
                  ? `Niveau ${currentLevel.level} · ${currentLevel.nameFr}`
                  : `Level ${currentLevel.level} · ${currentLevel.name}`}
              </p>

              {isContributorActive && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <ContributorBadge
                    language={language}
                    form={profile?.contributor_form || 'contribue'}
                  />
                </div>
              )}

              {/* Badges de stats */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-primary)]/10 text-[11px] font-semibold text-[color:var(--cb-text)] border border-[color:var(--cb-primary)]/20">
                  <Flag size={11} />
                  <span>
                    {progress.challengesCompleted}{' '}
                    {language === 'fr' ? 'défis' : 'challenges'}
                  </span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-accent-green)]/20 text-[11px] font-semibold text-[color:var(--cb-text)] border border-[color:var(--cb-accent-green)]/30">
                  <Flame size={11} />
                  <span>
                    {language === 'fr' ? `Série ${progress.currentStreak}` : `Streak ${progress.currentStreak}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 -mt-2">
              <Bongo mood={bongoMood} size="medium" idle />
            </div>
          </div>

          {/* Message de Bongo */}
          {dailyPhrase && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="mt-4 rounded-[var(--cb-radius-lg)] border border-[var(--cb-border)] bg-white backdrop-blur-sm p-4 shadow-[var(--cb-shadow-sm)]"
            >
              <p className="cozy-section-kicker mb-1.5" style={{ color: 'var(--color-primary)' }}>
                {language === 'fr' ? 'Message de Bongo' : 'Bongo note'}
              </p>
              <p
                className="leading-snug"
                style={{
                  fontFamily: '"Caveat", cursive',
                  fontSize: 17,
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                }}
              >
                {`"${dailyPhrase}"`}
              </p>
            </motion.div>
          )}

          {/* Barre de progression */}
          <div className="mt-4 pt-4 border-t border-[color:var(--cb-border)]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-[color:var(--cb-text-muted)] uppercase tracking-wider">
                {language === 'fr' ? 'Progression niveau' : 'Level progress'}
              </span>
              <span className="text-[11px] font-semibold text-[color:var(--cb-text-muted)]">
                {xpInLevel} / {xpForNextLevel} XP
              </span>
            </div>
            <XPBar
              currentXP={xpInLevel}
              maxXP={xpForNextLevel}
              level={currentLevel.level}
              levelName={language === 'fr' ? currentLevel.nameFr : currentLevel.name}
              t={t}
              showMeta={false}
            />
            <p className="text-[11px] text-[color:var(--cb-text-muted)] mt-2">
              {user
                ? (language === 'fr'
                  ? `Connecté · ${progressSource === 'cloud' ? 'Cloud' : 'Cache local'}${isProgressSyncing ? ' · Sync...' : ''}`
                  : `Signed in · ${progressSource === 'cloud' ? 'Cloud' : 'Local cache'}${isProgressSyncing ? ' · Syncing...' : ''}`)
                : (language === 'fr'
                  ? `Mode invité · Progression locale${isProgressSyncing ? ' · Sync...' : ''}`
                  : `Guest mode · Local progress${isProgressSyncing ? ' · Syncing...' : ''}`)}
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 max-w-md md:max-w-xl mx-auto mt-1">
        {/* CTA connexion pour les invités */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="cozy-card p-4 mb-4 border border-[rgba(232,168,124,0.25)] bg-[rgba(255,250,246,0.96)]"
            style={{ borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-[var(--cb-shadow-sm)])' }}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-10 h-10 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-primary)]/15 flex items-center justify-center">
                  <LogIn size={18} className="text-[color:var(--cb-primary)]" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-[color:var(--cb-text)] mb-1">
                  {language === 'fr'
                    ? 'Connecte-toi pour garder tes coloriages !'
                    : 'Sign in to keep your colorings!'}
                </p>
                <p className="text-xs text-[color:var(--cb-text-muted)] leading-relaxed mb-3">
                  {language === 'fr'
                    ? 'Tes dessins, ta progression et tes inspirations restent ici sur ton frigo. Gratuit et sans engagement.'
                    : 'Your drawings, progress and inspirations stay here on your fridge. Free and no commitment.'}
                </p>
                <motion.button
                  onClick={() => navigate('/auth')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[var(--cb-radius-pill)] bg-[var(--cb-peach-400)] text-[var(--cb-text-on-primary)] text-sm font-bold shadow-[var(--cb-shadow-md)]"
                  whileTap={{ scale: 0.97 }}
                >
                  <LogIn size={15} />
                  {language === 'fr' ? 'Se connecter' : 'Sign in'}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* PROMPT_55 v2 : la carte standalone CreatorCallBanner
            ("Ta technique merite d'etre une reference") a ete retiree
            d'ici — son CTA contributeur est desormais la derniere
            carte de <WhatsNewSection /> (item cta-contributeur dans
            data/whatsNew.js, stylise avec cta: true). */}

        {/* ── PROMPT_55 : nouvelle hierarchie ─────────────────────────────
            Spine de l'accueil : Quoi de neuf -> +Nouveau coloriage ->
            Installer (cond) -> Le Mur. PROMPT_56 : les separateurs
            visuels (3 traits 40px) ont ete retires — l'espacement
            entre sections suffit a guider l'oeil.
            ----------------------------------------------------------- */}

        {/* Annonce de pause du developpement - masquable, memorisee
            (colobongo_pause_notice_v1). Tous les outils restent en ligne. */}
        <PauseNoticeCard language={language} />

        {/* 2. Quoi de neuf */}
        <WhatsNewSection language={language} />

        {/* 3. On reprend doucement? — uniquement le bouton +Nouveau coloriage.
              Le listing des projets actifs et l'entree Frigo (BottomNav) ne
              sont plus rendus ici. Action quotidienne principale, identite
              visuelle verte distincte (kicker + bordure dashed). */}
        {user && (
          <section className="mb-2">
            <h2
              className="cozy-section-kicker mb-2"
              style={{ color: 'var(--color-accent-green)' }}
            >
              {language === 'fr' ? 'On reprend doucement?' : 'Pick up where you left off?'}
            </h2>
            <motion.button
              type="button"
              onClick={() => navigate('/carnet')}
              className="w-full flex items-center gap-3.5 text-left cozy-pressable"
              style={{
                backgroundColor: '#FFFFFF',
                border: '2px dashed var(--color-accent-green)',
                borderRadius: 'var(--radius-xl)',
                padding: '16px',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.97 }}
              data-testid="home-new-coloring-button"
              aria-label={language === 'fr' ? 'Nouveau coloriage' : 'New coloring'}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: 'rgba(168, 195, 160, 0.2)',
                  color: 'var(--color-accent-green)',
                  fontSize: '24px',
                  fontWeight: 800,
                  lineHeight: 1,
                }}
                aria-hidden="true"
              >
                +
              </div>
              <div className="min-w-0">
                <div
                  className="text-[15px] font-extrabold"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {language === 'fr' ? '+ Nouveau coloriage' : '+ New coloring'}
                </div>
                <div
                  className="text-xs mt-0.5"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {language === 'fr'
                    ? 'Note tes crayons pour mieux reprendre plus tard'
                    : 'Note your pencils to pick up later'}
                </div>
              </div>
            </motion.button>

            {/* PROMPT_62 : carte jeux adaptative.
                  Ne s'affiche pas tant que gamesCardState est null (chargement).
                  Etats : 'resume' (jeu joue < 30j) ou 'discover' (fallback). */}
            {gamesCardState && (
              <motion.button
                type="button"
                onClick={() => navigate(
                  gamesCardState.type === 'resume'
                    ? gamesCardState.game.route
                    : '/games'
                )}
                className="w-full flex items-center gap-3.5 text-left cozy-pressable mt-2.5"
                style={{
                  backgroundColor: 'rgba(255, 252, 249, 0.97)',
                  border: '1.5px solid rgba(149, 128, 208, 0.30)',
                  borderRadius: 'var(--radius-xl)',
                  padding: '16px',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-paper)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                whileTap={{ scale: 0.97 }}
                data-testid="home-games-card"
                aria-label={
                  gamesCardState.type === 'resume'
                    ? (language === 'fr'
                        ? `Reprends ${gamesCardState.game.nameFr} — Niveau ${gamesCardState.game.level}`
                        : `Resume ${gamesCardState.game.nameEn} — Level ${gamesCardState.game.level}`)
                    : (language === 'fr' ? 'Decouvre les jeux de Bongo' : 'Discover Bongo games')
                }
              >
                {/* Tinte lavande subtile en overlay */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(149, 128, 208, 0.04) 0%, transparent 60%)',
                    pointerEvents: 'none',
                    borderRadius: 'inherit',
                  }}
                />
                {/* Icone 48px */}
                <div
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    backgroundColor: 'rgba(149, 128, 208, 0.15)',
                    color: 'var(--cb-lavender-600, #6650A5)',
                  }}
                  aria-hidden="true"
                >
                  {gamesCardState.type === 'resume' && gamesCardState.game.icon === 'eye' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                  {gamesCardState.type === 'resume' && gamesCardState.game.icon === 'droplet' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                    </svg>
                  )}
                  {gamesCardState.type === 'discover' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  )}
                </div>
                {/* Contenu */}
                <div className="flex-1 min-w-0" style={{ position: 'relative' }}>
                  {gamesCardState.type === 'resume' && (
                    <>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '2px 8px',
                          borderRadius: 9999,
                          backgroundColor: 'rgba(149, 128, 208, 0.14)',
                          border: '1px solid rgba(149, 128, 208, 0.25)',
                          fontSize: 10,
                          fontWeight: 800,
                          color: 'var(--cb-lavender-600, #6650A5)',
                          letterSpacing: '0.02em',
                          marginBottom: 4,
                        }}
                        aria-label={language === 'fr'
                          ? `Niveau ${gamesCardState.game.level}`
                          : `Level ${gamesCardState.game.level}`}
                      >
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                          <line x1="4" y1="22" x2="4" y2="15"/>
                        </svg>
                        {language === 'fr'
                          ? `Niveau ${gamesCardState.game.level}`
                          : `Level ${gamesCardState.game.level}`}
                      </div>
                      <div
                        className="text-[15px] font-extrabold leading-tight"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {language === 'fr'
                          ? `${gamesCardState.game.nameFr} t'attend`
                          : `${gamesCardState.game.nameEn} is waiting`}
                      </div>
                      <div
                        className="text-xs mt-0.5"
                        style={{ color: 'var(--color-text-secondary)', lineHeight: 1.4 }}
                      >
                        {language === 'fr' ? 'Continue la ou tu t\'es arretee' : 'Pick up where you left off'}
                      </div>
                      <div
                        className="flex items-center gap-1.5 mt-1.5"
                        aria-label={language === 'fr'
                          ? `Progression : niveau ${gamesCardState.game.level} sur ${GAMES_LEVEL_MAX}`
                          : `Progress: level ${gamesCardState.game.level} of ${GAMES_LEVEL_MAX}`}
                      >
                        <div
                          style={{
                            flex: 1,
                            height: 4,
                            borderRadius: 9999,
                            backgroundColor: 'rgba(149, 128, 208, 0.14)',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              borderRadius: 9999,
                              background: 'linear-gradient(90deg, #9580D0, #C5B8E8)',
                              width: `${Math.min(100, ((gamesCardState.game.level - 1) / GAMES_LEVEL_MAX) * 100)}%`,
                              transition: 'width 400ms cubic-bezier(0.16, 1, 0.3, 1)',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: 'var(--cb-lavender-600, #6650A5)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {gamesCardState.game.level} / {GAMES_LEVEL_MAX}
                        </span>
                      </div>
                      {gamesCardState.game.streak > 1 && (
                        <div
                          style={{
                            fontSize: 10,
                            color: 'var(--color-text-tertiary)',
                            marginTop: 4,
                          }}
                          aria-label={language === 'fr'
                            ? `${gamesCardState.game.streak} jours d'affile`
                            : `${gamesCardState.game.streak} days in a row`}
                        >
                          {language === 'fr'
                            ? `${gamesCardState.game.streak} jours d'affile`
                            : `${gamesCardState.game.streak} days in a row`}
                        </div>
                      )}
                    </>
                  )}
                  {gamesCardState.type === 'discover' && (
                    <>
                      <div
                        className="text-[15px] font-extrabold"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {language === 'fr' ? 'Decouvre les jeux de Bongo' : 'Discover Bongo games'}
                      </div>
                      <div
                        className="text-xs mt-0.5"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        {language === 'fr'
                          ? "L'Oeil de Bongo · Trouve la Nuance — 60 niveaux chacun"
                          : "Bongo's Eye · Find the Shade — 60 levels each"}
                      </div>
                    </>
                  )}
                </div>
                {/* Chevron */}
                <svg
                  className="flex-shrink-0"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: 'rgba(149, 128, 208, 0.7)' }}
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </motion.button>
            )}
          </section>
        )}

        {/* PROMPT_56 : separateur +Nouveau -> Installer retire ici aussi. */}

        {/* 4. Installer — conditionnel, discret. Format compact existant. */}
        {!isAppInstalled && (
          <section className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <h2 className="cozy-section-kicker mb-0">
                {t('quickActions')}
              </h2>
            </div>

            <motion.button
              onClick={() => {
                try { localStorage.setItem('colobongo_visited_install_v1', '1'); } catch {}
                setHasVisitedInstall(true);
                navigate('/install');
              }}
              className="relative cozy-card cozy-interactive p-4 flex flex-col items-center gap-2 text-center w-full mt-2"
              whileTap={{ scale: 0.97 }}
              data-testid="home-install-button"
              aria-label={language === 'fr' ? "Installer ColoBongo sur mon écran d'accueil" : 'Install ColoBongo on my home screen'}
            >
              <div className="relative">
                {showInstallPulse && (
                  <motion.span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-[var(--cb-radius-pill)] pointer-events-none"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                    animate={{ opacity: [0.32, 0, 0.32], scale: [1, 1.4, 1] }}
                    transition={{ duration: 2.2, ease: 'easeInOut', repeat: Infinity }}
                  />
                )}
                <div
                  className="relative w-12 h-12 rounded-[var(--cb-radius-pill)] flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, var(--color-primary-soft), rgba(168,216,234,0.3))' }}
                >
                  <Smartphone size={22} className="text-[color:var(--color-primary)]" />
                </div>
              </div>
              <span className="text-sm font-semibold text-[color:var(--cb-text)]">
                {language === 'fr' ? 'Installer' : 'Install'}
              </span>
              {showInstallPulse && (
                <span
                  aria-hidden="true"
                  className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 rounded-full"
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    boxShadow: '0 0 0 3px var(--color-surface-soft)',
                  }}
                />
              )}
            </motion.button>
          </section>
        )}

        {/* 5. Le Mur — derniere section avant la bottom nav. Kicker bleu ciel
              externe pour identifier la zone. L'affichage des polaroids reste
              identique (HomeArtworkShowcase inchange). Espacement plus
              genereux (mt-6) au lieu d'un separateur pour marquer un
              changement d'univers. */}
        <section className="mt-6 mb-5">
          <h2
            className="cozy-section-kicker mb-2"
            style={{ color: 'var(--color-accent-sky)' }}
          >
            {language === 'fr' ? 'Le Mur' : 'The Wall'}
          </h2>
          <HomeArtworkShowcase language={language} />
        </section>

        {/* Onboarding étapes guidées — uniquement pour les nouveaux utilisateurs */}
        {isNewUser && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="cozy-card p-4 mb-4 border border-[rgba(181,201,154,0.3)] bg-[rgba(255,253,251,0.98)]"
            style={{ borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-[var(--cb-shadow-sm)])' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-extrabold text-[color:var(--cb-text)]">
                {language === 'fr' ? 'Par où commencer ?' : 'Where to start?'}
              </h3>
              <button
                onClick={() => {
                  setDismissedOnboarding(true);
                  try { localStorage.setItem('colobongo_dismissed_onboarding', 'true'); } catch {}
                }}
                className="p-1 rounded-[var(--cb-radius-pill)] hover:bg-[color:var(--cb-border)]/20 text-[color:var(--cb-text-muted)]"
                aria-label={language === 'fr' ? 'Fermer' : 'Close'}
              >
                <X size={14} />
              </button>
            </div>

            {/* Etape 1 — Défi du mois */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-primary)] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                1
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[color:var(--cb-text)] mb-0.5">
                  {language === 'fr'
                    ? 'Participe au défi du mois'
                    : 'Join the monthly challenge'}
                </p>
                <p className="text-xs text-[color:var(--cb-text-muted)] leading-relaxed mb-2">
                  {language === 'fr'
                    ? 'Un thème créatif, une inspiration, des XP à gagner. Lance-toi !'
                    : 'A creative theme, an inspiration, XP to earn. Get started!'}
                </p>
                <motion.button
                  onClick={() => navigate('/wheel')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-primary)] text-white text-xs font-bold"
                  whileTap={{ scale: 0.97 }}
                >
                  <Compass size={13} />
                  {language === 'fr' ? 'Voir le défi' : 'See the challenge'}
                </motion.button>
              </div>
            </div>

            {/* Séparateur */}
            <div className="border-t border-[var(--cb-border)] my-3" />

            {/* Etape 2 — Studio */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-accent-green)] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                2
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[color:var(--cb-text)] mb-0.5">
                  {language === 'fr'
                    ? 'Explore le Studio'
                    : 'Explore the Studio'}
                </p>
                <p className="text-xs text-[color:var(--cb-text-muted)] leading-relaxed mb-2">
                  {language === 'fr'
                    ? 'Tutoriels vidéo de textures et motifs, aide lumière 3D, inspirations AI et palettes de couleurs — ton atelier de coloriste pro !'
                    : 'Video texture & pattern tutorials, 3D light helper, AI inspirations and color palettes — your pro colorist studio!'}
                </p>
                <motion.button
                  onClick={() => navigate('/studio')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-accent-green)] text-white text-xs font-bold"
                  whileTap={{ scale: 0.97 }}
                >
                  <Sparkles size={13} />
                  {language === 'fr' ? 'Ouvrir le Studio' : 'Open Studio'}
                </motion.button>
              </div>
            </div>

            {/* Séparateur */}
            <div className="border-t border-[var(--cb-border)] my-3" />

            {/* Etape 3 — Colomunauté */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-accent-sky)] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                3
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[color:var(--cb-text)] mb-0.5">
                  {language === 'fr'
                    ? 'Découvre le Mur'
                    : 'Discover the Wall'}
                </p>
                <p className="text-xs text-[color:var(--cb-text-muted)] leading-relaxed mb-2">
                  {language === 'fr'
                    ? 'Inspire-toi des coloriages approuvés par la Colomunauté. Si tu veux partager un jour, un scan IA et ton consentement seront toujours demandés.'
                    : 'Get inspired by Colomunauty-approved colorings. If you want to share one day, an AI scan and your consent will always be required.'}
                </p>
                <motion.button
                  onClick={() => navigate('/mur')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-accent-sky)] text-white text-xs font-bold"
                  whileTap={{ scale: 0.97 }}
                >
                  <Users size={13} />
                  {language === 'fr' ? 'Voir le Mur' : 'See the Wall'}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {progress.activeChallenge && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="cozy-card cozy-interactive p-4 mb-4 cursor-pointer border-l-4 border-[color:var(--cb-primary)]"
            onClick={() => navigate('/challenge')}
            data-testid="home-active-challenge-card"
          >
            <p className="cozy-section-kicker mb-1">
              {t('activeChallenge')}
            </p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base font-bold text-[color:var(--cb-text)]">
                  {cName(progress.activeChallenge, language)}
                </p>
                <p className="text-xs text-[color:var(--cb-text-muted)] mt-1">
                  {cDesc(progress.activeChallenge, language)}
                </p>
              </div>
              <ChevronRight className="text-[color:var(--cb-primary)]" size={22} />
            </div>
          </motion.div>
        )}

        {/* Vos inspirations actuelles */}
        {user && (
          <section className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="cozy-section-kicker mb-0">
                {language === 'fr' ? 'Vos inspirations actuelles' : 'Your current inspirations'}
              </h2>
              <span className="text-xs text-[color:var(--cb-text-muted)]">{aiInspirations.length}/2</span>
            </div>

            {aiInspirations.length === 0 ? (
              <div className="cozy-card p-4 text-center">
                <Sparkles size={20} className="mx-auto mb-2 text-[color:var(--cb-text-muted)] opacity-50" />
                <p className="text-sm text-[color:var(--cb-text-muted)]">
                  {language === 'fr'
                    ? "Lance une inspiration depuis le Studio pour la retrouver ici."
                    : 'Run an inspiration from the Studio to find it here.'}
                </p>
                <motion.button
                  onClick={() => navigate('/studio/inspiration-ai')}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-primary)]/15 text-[color:var(--cb-text)] text-xs font-semibold"
                  whileTap={{ scale: 0.97 }}
                >
                  <Sparkles size={14} />
                  {language === 'fr' ? 'Ouvrir le Studio' : 'Open Studio'}
                </motion.button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {aiInspirations.slice(0, 2).map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="cozy-card p-2 border border-[var(--cb-border)]"
                  >
                    <div
                      className="relative rounded-[var(--cb-radius-lg)] overflow-hidden mb-2 bg-white cursor-pointer"
                      onClick={() => setViewingInspiration(item)}
                    >
                      <img
                        src={item.imageUrl}
                        alt={language === 'fr' ? 'Inspiration AI' : 'AI Inspiration'}
                        className="w-full h-28 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-[var(--cb-radius-pill)] bg-black/60 text-white text-[10px] font-bold flex items-center gap-1">
                        <Sparkles size={10} />
                        AI
                      </div>
                    </div>

                    <motion.button
                      type="button"
                      onClick={() => navigate('/studio/inspiration-ai', {
                        state: {
                          presetInspirationSource: {
                            imageUrl: item.imageUrl,
                            sourceType: 'recent_ai',
                            challengeCategory: item.challengeCategory || '',
                          }
                        }
                      })}
                      className="w-full py-2 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-primary)] text-white text-xs font-semibold"
                      whileTap={{ scale: 0.97 }}
                    >
                      {language === 'fr' ? 'Utiliser comme guide' : 'Use as guide'}
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      <AnimatePresence>
        {showEarlyAccessInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowEarlyAccessInfo(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[var(--cb-radius-xl)] p-6 max-w-sm w-full shadow-[var(--cb-shadow-lg)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-[var(--cb-radius-pill)] bg-[rgba(168,216,234,0.22)] flex items-center justify-center">
                    <Rocket size={20} className="text-[color:var(--cb-primary)]" />
                  </div>
                  <h3 className="text-lg font-extrabold text-[color:var(--cb-text)]">
                    {language === 'fr' ? 'Accès anticipé' : 'Early Access'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowEarlyAccessInfo(false)}
                  className="p-1 rounded-[var(--cb-radius-pill)] hover:bg-[var(--cb-brown-50)]"
                  aria-label={language === 'fr' ? 'Fermer' : 'Close'}
                >
                  <X size={20} className="text-[color:var(--cb-text-muted)]" />
                </button>
              </div>

              <div className="space-y-3 text-sm text-[color:var(--cb-text)]">
                {language === 'fr' ? (
                  <>
                    <p><strong>Bienvenue parmi les premiers utilisateurs !</strong></p>
                    <p>ColoBongo est encore en développement actif. Tu fais partie des premiers à l'essayer.</p>
                    <p>De nouvelles fonctionnalités arrivent régulièrement.</p>
                    <p>Tu pourrais rencontrer quelques bugs. Merci de ta patience !</p>
                    <p>Tes retours nous aident à améliorer l'app.</p>
                  </>
                ) : (
                  <>
                    <p><strong>Welcome, early adopter!</strong></p>
                    <p>ColoBongo is still in active development. You are among the first to try it.</p>
                    <p>New features are added regularly.</p>
                    <p>You might encounter some bugs, thanks for your patience.</p>
                    <p>Your feedback helps us improve the app.</p>
                  </>
                )}
              </div>

              <motion.button
                onClick={() => setShowEarlyAccessInfo(false)}
                className="w-full mt-5 py-3 rounded-[var(--cb-radius-pill)] bg-[color:var(--cb-primary)] text-white font-bold cozy-primary-cta cozy-pressable"
                whileTap={{ scale: 0.97 }}
              >
                {language === 'fr' ? 'Compris' : 'Got it'}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de création d'un coloriage en cours (Carnet de couleurs) */}
      <AnimatePresence>
        {showNewColoringModal && (
          <NewColoringProjectModal
            language={language}
            isContributor={!!isContributorActive}
            activeProjects={coloringProjects}
            onClose={() => setShowNewColoringModal(false)}
            onCreated={(projectId) => {
              setShowNewColoringModal(false);
              reloadColoring();
              navigate(`/carnet/${projectId}`);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
